import {BROWSER_TIERS, PROJECT_SUBTYPES, PROJECT_TYPES} from './constants';

const MILLISECONDS_PER_DAY = 1000 * 60 * 60 * 24;

/**
 * Truncates string if its length is longer than the given max length.
 */
export function truncate(value: string, maxLength: number): string {
  return value.substring(0, maxLength);
}

/**
 * Helper to make sure time milliSsecInMillisec being passed is sensible data.
 *
 * Any value under 0 (negative time?!) or over 86400000 (1 day in milliseconds)
 * is likely faulty data caused by a bug in the measurements.
 *
 * @throws Error if the input is not sensible.
 */
export function sensibleTimeValue(secInMillis: number): number {
  if (secInMillis >= 0 && secInMillis <= MILLISECONDS_PER_DAY) {
    return secInMillis;
  }

  throw new Error(
    `Analytics time value must be between 0 and ${MILLISECONDS_PER_DAY}.`,
  );
}

/**
 * Generates a random alpha-numeric string.
 *
 * @returns The generated random alpha-numeric string.
 */
export function getRandomString(): string {
  const x = 2147483648;
  return (
    Math.floor(Math.random() * x).toString(36) +
    Math.abs(Math.floor(Math.random() * x) ^ Date.now()).toString(36)
  );
}

/**
 * Generates a random alpha-numeric string of the given length.
 *
 * @param length The length of the random string.
 * @returns A random alpha-numeric string of the given length.
 */
export function randomString(length: number): string {
  let ret = getRandomString();
  while (ret.length < length) {
    ret += getRandomString();
  }
  return ret.substring(0, length);
}

type EnumConvertorFunc = (key: number | undefined | null) => string;

/**
 * Convert a given enum to a string.
 *
 * @param enums - The given enum need to be converted to string
 * @returns The converted string from the given enum
 */
export function convertEnumToString(
  enums: Map<number, string>,
): EnumConvertorFunc {
  return (key: number | undefined | null) => {
    if (typeof key !== 'number') {
      return '';
    }
    const val = enums.get(key);
    return val ? val : '';
  };
}

/**
 * Translates BrowserTier to string name.
 */
export const browserTierToString: EnumConvertorFunc =
  convertEnumToString(BROWSER_TIERS);

/**
 * Translates ProjectType to string name.
 */
export const projectTypeToString: EnumConvertorFunc =
  convertEnumToString(PROJECT_TYPES);

/**
 * Translates ProjectSubtype to string name.
 */
export const projectSubtypeToString: EnumConvertorFunc =
  convertEnumToString(PROJECT_SUBTYPES);
